import React, {
  useRef,
  useMemo,
  useCallback,
  useState,
  ReactNode,
} from "react";
import { a, m } from "kremling";
import styles from "./cp-data-table.styles.css";
import { TableHeaders } from "./table-headers.component";
import { TableBody } from "./table-body/table-body.component";
import { sortColumns } from "./cp-data-table.helper";
import { useEdgeScroll } from "./hooks/use-edge-scroll.hook";
import { Selection, useTableSelection } from "./hooks/use-table-selection.hook";
import { useBasicTableSelection } from "./hooks/use-basic-table-selection.hook";
import { useTableFilters } from "./hooks/use-table-filters.hook";
import { useTableSchema } from "./hooks/use-table-schema";
import {
  BoolCell,
  CurrencyCell,
  DateCell,
  TextCell,
  ListCell,
  LinkCell,
} from "./cells";
import {
  InputFilter,
  SortFilter,
  DateFilter,
  ConditionalSelectFilter,
  SelectFilter,
  BoolFilter,
} from "./filters";
import { CpDataTableFooter, CpDataTableActionBar } from "./sub-components";
import {
  ColumnSchema,
  ColumnWidthMap,
  FilterControl,
  ResizeInfo,
  Resource,
  TableSchema,
} from "./types";

export function CpDataTable({
  className,
  columnOrder,
  columnWidths,
  data,
  disabledResourceIds,
  filterControl,
  fullWidth = false,
  onColumnOrderChange,
  onColumnWidthsChange: onColumnWidthsChangeProp,
  onScroll,
  renderContextMenu,
  renderEmptyState,
  renderFooterRow,
  showLoader,
  schema,
  selection,
  visibleColumns,
}: CpDataTableProps) {
  const [, setTableContainer] = useState<HTMLDivElement>();
  const tableContainerRef = useRef<HTMLDivElement>();
  // Holds the actively resizing sticky column width since headers and body need to know this in order to set `left` on each cell.
  const [stickyResizing, setStickyResizing] = useState<ResizeInfo | null>(null);

  const usingSelection = !!selection;
  const orderedColumns = useMemo(() => {
    let cols;
    if (!visibleColumns || visibleColumns.length === 0) {
      // If no visible columns are provided then we want to show all columns.
      cols = Object.keys(schema);
    } else if (visibleColumns.includes("select")) {
      cols = visibleColumns;
    } else {
      // Currently old tables assume that if we're using selections then the select column is always shown.
      cols = visibleColumns.concat("select");
    }

    if (!usingSelection) {
      cols = cols.filter((col) => col !== "select");
    }

    // If select is not provided in the order then we always want to show it first.
    const selectFirstOrder = columnOrder?.includes("select")
      ? columnOrder
      : ["select", ...(columnOrder || [])];

    return sortColumns(schema, cols, selectFirstOrder);
  }, [columnOrder, schema, visibleColumns, usingSelection]);

  const { runEdgeScroller, stopEdgeScroller } = useEdgeScroll({
    scrollContainerRef: tableContainerRef,
    leftScrollAt: 280,
    rightScrollAt: 90,
    scrollSpeed: 2,
  });

  const onDrag = useCallback(
    (e: React.DragEvent<HTMLTableCellElement>) => {
      runEdgeScroller(e);
    },
    [runEdgeScroller],
  );

  const onDragEnd = useCallback(() => {
    stopEdgeScroller();
  }, [stopEdgeScroller]);

  const setContainerRef = useCallback((node: HTMLDivElement) => {
    // Using state and a ref so that once we have a ref to the container we trigger an update so that the virtualizer knows of the ref and can use it.
    setTableContainer(node);
    tableContainerRef.current = node;
  }, []);

  const onResize = useCallback((colSchema: ColumnSchema, newWidth: number) => {
    if (!colSchema.sticky) return;
    setStickyResizing({
      columnId: colSchema.id,
      width: newWidth,
    });
  }, []);

  const onColumnWidthsChange = useCallback(
    (widths: ColumnWidthMap) => {
      onColumnWidthsChangeProp?.(widths);
      setStickyResizing(null);
    },
    [onColumnWidthsChangeProp],
  );

  return (
    <div
      ref={setContainerRef}
      className={a(className).a(styles.tableContainer)}
      onScroll={onScroll}
    >
      <table
        className={m(styles.tableWithEmptyState, renderEmptyState)
          .a(styles.table)
          .m(styles.tableWithFooter, !!renderFooterRow)
          .m("cp-data-table--full-width", fullWidth)}
      >
        <TableHeaders
          columnWidths={columnWidths}
          filterControl={filterControl}
          onColumnOrderChange={onColumnOrderChange}
          onColumnWidthsChange={onColumnWidthsChange}
          onDrag={onDrag}
          onDragEnd={onDragEnd}
          onResize={onResize}
          orderedColumns={orderedColumns}
          schema={schema}
          selection={selection}
          verticalBorders={!!onColumnWidthsChangeProp}
        />
        <TableBody
          columnWidths={columnWidths}
          data={data}
          disabledResourceIds={disabledResourceIds}
          orderedColumns={orderedColumns}
          renderContextMenu={renderContextMenu}
          renderEmptyState={renderEmptyState}
          showLoader={showLoader}
          schema={schema}
          selection={selection}
          stickyResizing={stickyResizing}
          tableContainerRef={tableContainerRef}
          verticalBorders={!!onColumnWidthsChangeProp}
        />
        {renderFooterRow && (
          <tfoot>
            <tr className={styles.footerRow}>{renderFooterRow()}</tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// Hooks
CpDataTable.useSchema = useTableSchema;
CpDataTable.useSelection = useTableSelection;
CpDataTable.useBasicSelection = useBasicTableSelection;
CpDataTable.useFilters = useTableFilters;

// Cells
CpDataTable.BoolCell = BoolCell;
CpDataTable.CurrencyCell = CurrencyCell;
CpDataTable.TextCell = TextCell;
CpDataTable.DateCell = DateCell;
CpDataTable.ListCell = ListCell;
CpDataTable.LinkCell = LinkCell;

// Filters
CpDataTable.InputFilter = InputFilter;
CpDataTable.SortFilter = SortFilter;
CpDataTable.DateFilter = DateFilter;
CpDataTable.ConditionalSelectFilter = ConditionalSelectFilter;
CpDataTable.SelectFilter = SelectFilter;
CpDataTable.BoolFilter = BoolFilter;

// Sub-components
CpDataTable.Footer = CpDataTableFooter;
CpDataTable.ActionBar = CpDataTableActionBar;

interface CpDataTableProps {
  /**
   * An array containing the resources to be displayed in the table. Each item must have an `id` property.
   */
  data: Resource[];

  /**
   * An object returned from `CpDataTable.useSchema` outlining info and configuration details for the table columns. Check out the [Schema page](/?path=/docs/components-cpdatatable-schema--docs) for more details.
   */
  schema: TableSchema;

  /** className applied to the container div around the table element. */
  className?: string;

  /** An array of column IDs in the order they should be displayed. */
  columnOrder?: string[];

  /** A map of column IDs to the width. The width value can be a size string (sm, md, lg) or a number representing a pixel value. */
  columnWidths?: ColumnWidthMap;

  /** An array of resource ids that are considered disabled */
  disabledResourceIds?: (string | number)[];

  /**
   * Filter controller object returned from `CpDataTable.useFilters`. If provided, the columns will default to using the filter header, otherwise it will use a basic text header.
   */
  filterControl?: FilterControl;

  /** If true the column widths will attempt to fill the width of the table container. If you're wanting to support table resizing and dynamic columns then you probably will want this set to false. Otherwise if your table widths and column counts are fixed then you'll most likely want this set to true. */
  fullWidth?: boolean;

  /**
   * Called when the user has changed the column order.
   * @param {array} columnIds - Array of column IDs in the new order.
   */
  onColumnOrderChange?: (columnIds: string[]) => void;

  /**
   * Called when the user has changed the column widths.
   * @param {object} columnWidths - Map of column IDs to the new widths.
   */
  onColumnWidthsChange?: (columnWidths: ColumnWidthMap) => void;

  /** Callback passed to the `onScroll` event of the containing div. */
  onScroll?: React.UIEventHandler<HTMLDivElement>;

  /**
   * If provided, `onContextMenu` will be overridden and will display a `CpFloatingMenu` with this method being called as the child of the `CpFloatingMenu` component.
   */
  renderContextMenu?: ({
    clickedResource,
  }: {
    clickedResource: Resource;
  }) => ReactNode;

  /** If provided, the table will only render the headers and the body will be replaced with the result of this function.  */
  renderEmptyState?: () => ReactNode;

  /** If provided, the table will render a footer row with the result of this function in a `<tr>` tag. */
  renderFooterRow?: () => ReactNode;

  /** If true then the table body will show a loader. */
  showLoader?: boolean;

  /**
   * Selection object returned from `CpDataTable.useSelection`. If provided, the table will render a sticky selection column.
   */
  selection?: Selection;

  /** An array of columns IDs that should be visible. If empty or falsey then all columns will be visible. */
  visibleColumns?: string[];
}
